/**
 * The one way this app talks to the Hawfinch API.
 *
 * `openapi-fetch` is typed entirely from `schema.d.ts`, which is generated
 * from the server's own `/openapi.json`. Paths, query params and response
 * bodies are all checked against the spec at compile time: a typo in a path,
 * a missing query param, or reading a field the server does not return is a
 * TypeScript error rather than an empty column in the browser.
 *
 * Nothing else in this app may call `fetch` directly. That rule is what keeps
 * the client from re-growing hand-written copies of the server's shapes,
 * which is the bug class the whole migration exists to close.
 */
import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { validated } from './validate';

/** Where the session lives. Same keys the Alpine app uses, different origin. */
const TOKEN_KEY = 'hawfinch_token';
const API_URL_KEY = 'hawfinch_api_url';

// VITE_API_URL / VITE_GATEWAY_URL let a local run point at local servers.
export const DEFAULT_API_URL: string = import.meta.env.VITE_API_URL || 'https://api.hawfinch.com';
export const GATEWAY_URL: string = import.meta.env.VITE_GATEWAY_URL || 'https://gateway.hawfinch.com';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getApiUrl(): string {
  try {
    return localStorage.getItem(API_URL_KEY) || DEFAULT_API_URL;
  } catch {
    return DEFAULT_API_URL;
  }
}

export function storeSession(token: string, apiUrl: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(API_URL_KEY, apiUrl);
  } catch {
    /* private mode — the session simply won't survive a reload */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(API_URL_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Raised on any non-2xx. TanStack Query keys off thrown errors. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = createClient<paths>({ baseUrl: getApiUrl() });

api.use({
  onRequest({ request }) {
    const token = getToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
});

/**
 * Unwrap an openapi-fetch result into the data, or throw.
 *
 * The server emits RFC 9457 `application/problem+json` on real status codes,
 * so `error.detail` carries a human-readable message and `error.type` a stable
 * identifier. Throwing is what lets TanStack Query distinguish a failed
 * mutation from a successful one — a 200 carrying `success:false` would read
 * to it as success, fire `onSuccess`, and show a confirmation toast for an
 * operation that did not happen.
 */
export function unwrap<T>(
  result: {
    data?: T;
    error?: unknown;
    response: Response;
  },
  /**
   * Name of the schema in `api/zod.ts` this response should match. Optional:
   * an unvalidated call is no worse than before, and passing a name upgrades
   * it to a checked one.
   */
  schemaName?: string,
): T {
  const { status, url } = result.response;

  if (result.error !== undefined) {
    // RFC 9457 problem document, or the legacy {success:false,error} shape.
    const e = result.error as { detail?: string; title?: string; error?: string };
    throw new ApiError(
      e.detail ?? e.title ?? e.error ?? `Request failed (${status})`,
      status,
    );
  }

  if (result.data === undefined) {
    // The request completed but produced no parsable body. Always say what the
    // status and path were: a bare "Empty response" is unactionable, and the
    // most common cause is calling a /api/v1 route against a server that
    // predates it, which looks identical to a network fault without this.
    if (status === 401) {
      throw new ApiError('Session expired or not authorised. Sign in again.', status);
    }
    if (status === 404) {
      throw new ApiError(
        `Endpoint not found (404): ${new URL(url).pathname}. ` +
          'The API server may be older than this build of the web app.',
        status,
      );
    }
    throw new ApiError(
      `No response body (HTTP ${status}) from ${new URL(url).pathname}`,
      status,
    );
  }

  return schemaName ? validated(schemaName, result.data) : result.data;
}
